class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    try {
      if (typeof subscribe !== 'function') return;
      const events = typeof PUB_SUB_EVENTS !== 'undefined' ? PUB_SUB_EVENTS : null;
      if (!events || !events.cartUpdate) return;
      this._cartUpdateUnsub = subscribe(events.cartUpdate, () => {
        setTimeout(() => this.updateProgressBar(true), 250);
      });
    } catch (e) {}
  }

  disconnectedCallback() {
    if (this._cartUpdateUnsub) this._cartUpdateUnsub();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    // Refresh progress bar when drawer opens so green fill reflects current cart total
    setTimeout(() => this.updateProgressBar(false), 100);

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });

    // Progress bar: run after paint (double rAF) and again after delays so it always updates when add-to-cart
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.updateProgressBar(true);
      });
    });
    setTimeout(() => this.updateProgressBar(true), 300);
    setTimeout(() => this.updateProgressBar(true), 650);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  updateProgressBar(animate = false) {
    const progressEl = this.querySelector('#cartProgress') || document.getElementById('cartProgress');
    if (!progressEl) return;
    const threshold = parseFloat(progressEl.getAttribute('data-threshold')) || 1000;
    const message = progressEl.getAttribute('data-message') || 'Free shipping';
    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => {
        const cartTotal = cart.total_price / 100;
        const isComplete = cartTotal >= threshold;
        const pct = Math.min(100, Math.max(0, (cartTotal / threshold) * 100));
        const remaining = isComplete ? 0 : Math.max(0, threshold - cartTotal);
        const messageEl = progressEl.querySelector('.cart-progress__message');
        const progressBar = progressEl.querySelector('.progress-bar') || document.getElementById('cartProgressBar');
        if (!messageEl || !progressBar) return;
        progressEl.classList.toggle('is-complete', isComplete);
        messageEl.innerHTML = isComplete
          ? `Congratulations! You've unlocked <strong>${message || 'FREE SHIPPING'}</strong>!`
          : `Spend Rs. ${Math.round(remaining)} more to get <strong>FREE SHIPPING!</strong>`;
        progressBar.setAttribute('data-progress', isComplete ? 'complete' : 'progress');
        progressBar.style.setProperty('--progress-pct', `${Math.round(pct * 10) / 10}%`);
        if (animate) {
          progressBar.classList.add('animate-fill');
          setTimeout(() => progressBar.classList.remove('animate-fill'), 600);
        }
      })
      .catch(() => {});
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
